import Vue from 'vue'
import VueRouter from 'vue-router'
import { Auth } from 'aws-amplify'
import store from '../store/index.js'
import dayjs from 'dayjs'

Vue.use(VueRouter)

const routes = [
  {
    path: '/login',
    component: () => import('../components/AuthLayout.vue'),
    children: [
      {
        name: 'login',
        path: '',
        component: () => import('../views/auth/Authenticator.vue')
      },
      {
        name: 'maintenance',
        path: '/maintenance',
        component: () => import('../views/auth/Maintenance.vue')
      },
      {
        name: 'splash',
        path: '/splash',
        component: () => import('../views/auth/Splash.vue')
      }
    ]
  },
  {
    path: '',
    component: () => import('../components/MainLayout.vue'),
    children: [
      {
        name: 'course',
        path: '/course',
        component: () => import('../views/course/Course.vue'),
        meta: {
          requiresAuth: true
        },
        props: true
      },
      {
        name: 'result',
        path: '/result',
        component: () => import('../views/result/Result.vue'),
        meta: {
          requiresAuth: true
        }
      },
      {
        name: 'team',
        path: '/team',
        component: () => import('../views/team/Team.vue'),
        meta: {
          requiresAuth: true
        }
      },
      {
        name: 'teamRanking',
        path: '/teamRanking',
        component: () => import('../views/team/TeamRanking.vue'),
        meta: {
          requiresAuth: true
        }
      },
      {
        name: 'board',
        path: '/board',
        component: () => import('../views/board/Board.vue'),
        meta: {
          requiresAuth: true
        }
      },
      {
        name: 'process_participation',
        path: '/process_participation',
        component: () => import('../views/process/Participation.vue'),
        meta: {
          requiresAuth: true
        }
      },
      {
        name: 'process_course',
        path: '/process_course',
        component: () => import('../views/process/Course.vue'),
        meta: {
          requiresAuth: true
        }
      },
      {
        name: 'course_pack',
        path: '/course_pack',
        component: () => import('../views/course/CoursePack.vue'),
        meta: {
          requiresAuth: true
        }
      },
      {
        name: 'app_entry',
        path: '/app_entry',
        component: () => import('../views/app/AppEntry.vue'),
        meta: {
          requiresAuth: true
        }
      },
      {
        name: 'question',
        path: '/question',
        component: () => import('../views/question/Question.vue'),
        meta: {
          requiresAuth: true
        }
      },
      {
        name: 'manage',
        path: '/manage',
        component: () => import('../views/manage/Manage.vue'),
        meta: {
          requiresAuth: true
        }
      },
      {
        name: 'learning',
        path: '/learning',
        component: () => import('../views/learning/Learning.vue'),
        meta: {
          requiresAuth: true
        }
      },
      {
        name: 'prize',
        path: '/prize',
        component: () => import('../views/prize/Prize.vue'),
        meta: {
          requiresAuth: true
        }
      },
      {
        name: 'email',
        path: '/email',
        component: () => import('../views/email/Email.vue'),
        meta: {
          requiresAuth: true
        }
      },
      {
        name: 'seconded',
        path: '/seconded',
        component: () => import('../views/seconded/Seconded.vue'),
        meta: {
          requiresAuth: true
        }
      },
      {
        name: 'start',
        path: '/start',
        component: () => import('../views/start/Start.vue'),
        meta: {
          requiresAuth: true
        }
      }
    ]
  }
]

const router = new VueRouter({
  mode: 'history',
  hash: false,
  base: process.env.BASE_URL,
  routes
})

// implement protected routes for only signed in users
router.beforeResolve((to, from, next) => {
  if (to.matched.some(record => record.meta.requiresAuth)) {
    Auth.currentAuthenticatedUser().then(async (data) => {
      if (data && data.signInUserSession) {
        const campaign = store.getters.getCampaign

        // コース選択済みではないとき、遷移先に制御あり
        if (!store.getters.getRegisteredCourse) {
          const appEntryStatus = store.getters.getAppEntryStatus
          if ((appEntryStatus === null || appEntryStatus === 'Unregistered' || appEntryStatus === 'Canceled') &&
            (to.name === 'result' || to.name === 'team' || to.name === 'board' || to.name === 'question')) {
            next({ name: 'course' })
          } else if ((appEntryStatus === 'Temporary' || appEntryStatus === 'Waiting' || appEntryStatus === 'Completion') &&
            (to.name === 'team' || to.name === 'question')) {
            next({ name: 'course' })
          }
        }

        // チーム機能ありの場合のみ遷移可能
        if (to.name === 'team') {
          if (campaign.is_team) {
            next()
          } else {
            next({ name: 'result' })
          }

        // 掲示板機能ありの場合のみ遷移可能
        } else if (to.name === 'board') {
          if (campaign.is_board) {
            next()
          } else {
            next({ name: 'result' })
          }

        // eラーニング機能ありの場合のみ遷移可能
        } else if (to.name === 'learning' || to.name === 'rizap') {
          if (campaign.is_learning) {
            next()
          } else {
            next({ name: 'result' })
          }

        // 達成賞機能ありの場合のみ遷移可能
        } else if (to.name === 'prize') {
          if (campaign.is_prize) {
            next()
          } else {
            next({ name: 'result' })
          }

        // アンケート機能ありかつアンケート期間中の場合のみ遷移可能
        } else if (to.name === 'question') {
          const startDate = dayjs(campaign.questionnaire_start, 'YYYY-MM-DD')
          const endDate = dayjs(campaign.questionnaire_end, 'YYYY-MM-DD').add(1, 'days')
          if (campaign.is_questionnaire && startDate <= dayjs() && dayjs() < endDate) {
            next()
          } else {
            next({ name: 'result' })
          }

        // アプリチャレンジありの場合のみ遷移可能
        } else if (to.name === 'app_entry') {
          if (campaign.is_app_challenge) {
            next()
          } else {
            next({ name: 'result' })
          }

        // メールアドレス変更時、認証されていないユーザーが他の画面に遷移できない
        } else if (to.name !== 'login' && to.name !== 'email' && from.name === 'email' && !store.getters.getEmailVerified) {
          next({ path: '/email' })
        } else {
          next()
        }
      } else {
        next({ path: '/login' })
      }
    }).catch((e) => {
      next({
        path: '/login',
        query: {
          redirect: to.fullPath
        }
      })
    })
  } else {
    if (to.fullPath === '/') {
      next({
        path: '/login'
      })
    } else {
      next()
    }
  }
})

router.onError(error => {
  if (/(?=^Loading chunk chunk-)(?=.*failed)/i.test(error.message) && navigator.onLine) {
    window.location.reload()
  }
})

export default router
